// src/Router.jsx
import { Suspense, lazy } from "react";
import { useSelector } from "react-redux";
import { Navigate, useRoutes } from "react-router-dom";
import Loader from "../components/Loader";
import AuthLayout from "../layouts/AuthLayout";
import DefaultLayout from "../layouts/DefaultLayout";
import OnBoardingLayout from "../layouts/OnBoardingLayout";
import UserLayout from "../layouts/UserLayout";
import NotFound from "../pages/user/NotFound";
// Lazy loaded pages
const Login = lazy(() => import("../pages/auth/Login"));
const SignUp1 = lazy(() => import("../pages/auth/SignUp1"));
const OnBoarding = lazy(() => import("../pages/OnBoarding"));
const ResidentDefaultDashboard = lazy(() =>
  import("../pages/auth/ResidentDefaultDashboard")
);
const extractSlug = (str) => {
  const parts = str.split("/").filter(Boolean);
  return parts.slice(0, 2).join("/");
};
// Config routes
import { protectedRoutes, standaloneRoutes } from "../routes/config";

// Fallback
const Loading = () => <Loader />;

// Helper: Check menu access
const hasRouteAccess = (menu, routeKey) => {
  if (!menu) return false;

  const search = (items) => {
    for (const item of items) {
      if (item.router && item.router.toLowerCase() === routeKey.toLowerCase()) {
        return item.selectedApplicationAccessControlList?.length > 0;
      }
      if (item.subModules?.length > 0) {
        const found = search(item.subModules);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  };

  return search(menu) === true;
};

export default function Router() {
  const { isLogin, isSkip, roleName, menu } = useSelector(
    (state) => state.authReducer
  );

  console.log("RoleName", roleName);
  // Always allowed (even without menu)
  const alwaysAllowed = new Set([
    "",
    "resident-announcements",
    "create-community",
    "maintenance-report",
    "dues-report",
    "reports",
    "changePassword",
    "termsNConditions",
    "privacyPolicy",
    "notifications",
    "amenityDetails",
  ]);

  const checkWalletChild = (segment) => {
    const walletChildren = [
      "transaction-history",
      "beneficiaries",
      "withdraw",
      "schedule-of-charges",
    ];
    return walletChildren.includes(segment) && hasRouteAccess(menu, "wallet");
  };

  const checkSubscriptionRoute = (segment) => {
    const subsRelatedRoute = [
      "subscription-timer",
      "subscription-update",
      "helps",
    ];
    return (
      subsRelatedRoute.includes(segment) && hasRouteAccess(menu, "subscription")
    );
  };

  const checkReportRoute = (segment) => {
    const newSegment = extractSlug(segment);
    console.log("The comming segment", segment);
    const reportRelatedRoute = [
      "report/current-dues-report",
      "report/income-expense-report",
      "report/expense",
      "report/accounts",
      "report/account",
    ];
    return (
      reportRelatedRoute.includes(newSegment) && hasRouteAccess(menu, "reports")
    );
  };

  const isRouteAllowed = (route) => {
    let routePath = route.path;
    console.log("RoutePath", routePath);
    const segment = (routePath.split("/")[1] || "").split("?")[0];

    if (alwaysAllowed.has(segment)) return true;
    if (checkWalletChild(segment)) return true;
    if (checkSubscriptionRoute(segment)) return true;
    if (checkReportRoute(routePath.split("?")[0])) return true;

    return hasRouteAccess(menu, segment);
  };

  // 1. NOT LOGGED IN → Auth Routes
  if (!isLogin) {
    return useRoutes([
      {
        path: "/",
        element: <AuthLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <Login />
              </Suspense>
            ),
          },
          {
            path: "",
            element: (
              <Suspense fallback={<Loading />}>
                <Login />
              </Suspense>
            ),
          },
          {
            path: "signup",
            element: (
              <Suspense fallback={<Loading />}>
                <SignUp1 />
              </Suspense>
            ),
          },
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ]);
  }

  // 2. LOGGED IN BUT NOT SKIPPED → Onboarding
  if (!isSkip) {
    return useRoutes([
      {
        path: "/",
        element: <OnBoardingLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <OnBoarding />
              </Suspense>
            ),
          },
          { path: "*", element: <NotFound /> },
        ],
      },
    ]);
  }

  // 3. QP_CB_User (default community user)
  if (roleName === "QP_CB_User") {
    console.log("QP_CB_User...");
    return useRoutes([
      {
        path: "/",
        element: <DefaultLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <OnBoarding />
              </Suspense>
            ),
          },
          {
            path: "default-dashboard",
            element: <UserLayout />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<Loading />}>
                    {" "}
                    <ResidentDefaultDashboard />
                  </Suspense>
                ),
              },
            ],
          },
          { path: "*", element: <NotFound /> },
        ],
      },
    ]);
  }

  // 4. FULLY AUTHORIZED USER → RBAC Protected App
  const allowedProtectedRoutes = protectedRoutes
    .filter(isRouteAllowed)
    .map((route) => ({
      path: route.path,
      element: <Suspense fallback={<Loading />}>{route.element}</Suspense>,
    }));

  const finalRoutes = [
    // Standalone (outside UserLayout)
    ...standaloneRoutes.map((r) => ({
      path: r.path,
      element: <Suspense fallback={<Loading />}>{r.element}</Suspense>,
    })),

    // Main App
    {
      path: "/",
      element: <UserLayout />,
      children: [
        ...allowedProtectedRoutes,
        { path: "*", element: <NotFound /> },
      ],
    },

    // Catch all
    { path: "*", element: <Navigate to="/not-found" replace /> },
  ];

  return useRoutes(finalRoutes);
}
